import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Navbar from '../components/Navbar'
import Footer from '../components/Footer'
import { useAuth } from '../contexts/AuthContext'
import { executeCapability } from '../utils/capabilityExecute'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSpinner } from '@fortawesome/free-solid-svg-icons'
import {
  canAccessTallyApp,
  canManageTally as canManageTallyPerm,
  canViewTallyAssets,
  canViewTallyFleet,
  canViewTallyInventory,
} from '../utils/tallyPermissions'
import TallyToolbar from '../components/tally/TallyToolbar'
import TallyItemList from '../components/tally/TallyItemList'
import TallyItemModal from '../components/tally/TallyItemModal'
import TallyMobileActions from '../components/tally/TallyMobileActions'
import TallyBulkBar from '../components/tally/TallyBulkBar'
import TallyBulkEditModal, { type TallyBulkEditValues } from '../components/tally/TallyBulkEditModal'
import TallyBulkDeleteConfirm from '../components/tally/TallyBulkDeleteConfirm'
import BarcodeScanner from '../components/tally/BarcodeScanner'
import FleetTab from '../components/tally/fleet/FleetTab'
import { TALLY_ALERT_MS } from '../components/tally/useAutoDismiss'
import type { InventoryAddDraft } from '../components/tally/InventoryAddFlow'
import type {
  AssetOrInventory,
  EmployeeAssignmentOption,
  InventoryHistoryEntry,
  JobTreadJobOption,
  TallyModalMode,
  TallyTab,
} from '../components/tally/types'
import { compressImageFiles, errorMessage, formatJobLabel, getDocuments, getImageUrls, locationAddressFromJob, buildTallySearchHaystack, matchesTallySearch, readDocumentFiles } from '../components/tally/types'

interface TallyPageProps {
}

const emptyInventoryDraft = (): InventoryAddDraft => ({
  name: '',
  barcode: '',
  notes: '',
  quantity: 1,
  status: 'in_stock',
  imageUrls: [],
  documents: [],
  job: null,
})

function asTallyItem(raw: unknown, source: 'Asset' | 'Inventory'): AssetOrInventory {
  const base = (raw && typeof raw === 'object' ? raw : {}) as Partial<AssetOrInventory>
  return {
    _id: String(base._id || ''),
    name: String(base.name || ''),
    type: String(base.type || ''),
    ...base,
    source,
  }
}

const TallyPage: React.FC<TallyPageProps> = () => {
  const navigate = useNavigate()
  const { loading: authLoading, hasPermission } = useAuth()
  const canViewAssets = canViewTallyAssets(hasPermission)
  const canViewInventory = canViewTallyInventory(hasPermission)
  const canViewFleet = canViewTallyFleet(hasPermission)
  const canManageTally = canManageTallyPerm(hasPermission)
  const hasAnyTallyPermission = canAccessTallyApp(hasPermission)
  const canRequestEmployeeAssignments = hasAnyTallyPermission && (canViewAssets || canViewInventory)

  const [tab, setTab] = useState<TallyTab>(() => {
    if (canViewFleet) return 'fleet'
    if (canViewInventory) return 'inventory'
    if (canViewAssets) return 'assets'
    return 'fleet'
  })
  const [search, setSearch] = useState('')
  const [filters, setFilters] = useState<string[]>([])
  const [collapsedView, setCollapsedView] = useState(false)
  const [assets, setAssets] = useState<AssetOrInventory[]>([])
  const [inventory, setInventory] = useState<AssetOrInventory[]>([])
  const [employeeOptions, setEmployeeOptions] = useState<EmployeeAssignmentOption[]>([])
  const [loadingAssets, setLoadingAssets] = useState(false)
  const [loadingInventory, setLoadingInventory] = useState(false)
  const [errorAssets, setErrorAssets] = useState<string | null>(null)
  const [errorInventory, setErrorInventory] = useState<string | null>(null)
  const [toast, setToast] = useState<string | null>(null)
  const toastTimerRef = useRef<number | null>(null)

  const [selectedItem, setSelectedItem] = useState<AssetOrInventory | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [modalMode, setModalMode] = useState<TallyModalMode>('view')
  const [editingItem, setEditingItem] = useState<AssetOrInventory | null>(null)
  const [savingItem, setSavingItem] = useState(false)
  const [deletingItem, setDeletingItem] = useState(false)
  const [galleryIndex, setGalleryIndex] = useState(0)
  const [modalMessage, setModalMessage] = useState<string | null>(null)
  const [modalTab, setModalTab] = useState<'info' | 'history'>('info')
  const [showCheckTo, setShowCheckTo] = useState(false)
  const [inventoryAddDraft, setInventoryAddDraft] = useState<InventoryAddDraft>(emptyInventoryDraft())
  const [scannerOpen, setScannerOpen] = useState(false)
  const [scannerPurpose, setScannerPurpose] = useState<'lookup' | 'add' | 'edit'>('lookup')
  const [history, setHistory] = useState<InventoryHistoryEntry[]>([])
  const [historyLoading, setHistoryLoading] = useState(false)
  const employeeAssignmentsLoadedRef = useRef(false)

  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set())
  const [bulkEditOpen, setBulkEditOpen] = useState(false)
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false)
  const [bulkBusy, setBulkBusy] = useState(false)

  const clearSelection = useCallback(() => setSelectedIds(new Set()), [])

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const toggleSelectAll = useCallback((ids: string[], selectAll: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (selectAll) ids.forEach((id) => next.add(id))
      else ids.forEach((id) => next.delete(id))
      return next
    })
  }, [])

  const showToast = useCallback((msg: string) => {
    setToast(msg)
    if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current)
    toastTimerRef.current = window.setTimeout(() => {
      setToast(null)
      toastTimerRef.current = null
    }, TALLY_ALERT_MS)
  }, [])

  useEffect(() => {
    if (!modalMessage) return
    const id = window.setTimeout(() => setModalMessage(null), TALLY_ALERT_MS)
    return () => window.clearTimeout(id)
  }, [modalMessage])

  const loadAssets = useCallback(async () => {
    setLoadingAssets(true)
    setErrorAssets(null)
    try {
      const assetsData = await executeCapability<AssetOrInventory[]>({ app: 'tally', capability: 'assets.list', input: {} })
      setAssets((assetsData || []).map((a) => ({ ...a, source: 'Asset' as const })))
    } catch (err: unknown) {
      setErrorAssets(errorMessage(err, 'Failed to fetch assets'))
    } finally {
      setLoadingAssets(false)
    }
  }, [])

  const loadInventory = useCallback(async () => {
    setLoadingInventory(true)
    setErrorInventory(null)
    try {
      const inventoryData = await executeCapability<AssetOrInventory[]>({
        app: 'tally',
        capability: 'inventory.list',
        input: {},
      })
      setInventory((inventoryData || []).map((i) => ({ ...i, source: 'Inventory' as const })))
    } catch (err: unknown) {
      setErrorInventory(errorMessage(err, 'Failed to fetch inventory'))
    } finally {
      setLoadingInventory(false)
    }
  }, [])

  const loadEmployeeAssignments = useCallback(async () => {
    try {
      const employees = await executeCapability<EmployeeAssignmentOption[]>({
        app: 'tally',
        capability: 'assignments.listEmployees',
        input: {},
      })
      setEmployeeOptions(Array.isArray(employees) ? employees : [])
      return true
    } catch {
      setEmployeeOptions([])
      return false
    }
  }, [])

  const loadHistory = useCallback(async (id: string) => {
    if (!id) {
      setHistory([])
      return
    }
    setHistoryLoading(true)
    try {
      const rows = await executeCapability<InventoryHistoryEntry[]>({
        app: 'tally',
        capability: 'inventory.listHistory',
        input: { id },
      })
      setHistory(Array.isArray(rows) ? rows : [])
    } catch {
      setHistory([])
    } finally {
      setHistoryLoading(false)
    }
  }, [])

  useEffect(() => {
    if (tab === 'assets') void loadAssets()
    else if (tab === 'inventory') void loadInventory()
  }, [tab, loadAssets, loadInventory])

  useEffect(() => {
    if (!canRequestEmployeeAssignments) return
    if (employeeAssignmentsLoadedRef.current) return
    employeeAssignmentsLoadedRef.current = true
    void loadEmployeeAssignments().then((ok) => {
      if (!ok) employeeAssignmentsLoadedRef.current = false
    })
  }, [canRequestEmployeeAssignments, loadEmployeeAssignments])

  useEffect(() => {
    if (modalOpen && selectedItem?.source === 'Inventory' && selectedItem._id && modalTab === 'history') {
      void loadHistory(selectedItem._id)
    }
  }, [modalOpen, selectedItem, modalTab, loadHistory])

  const closeModal = useCallback(() => {
    setModalOpen(false)
    setShowCheckTo(false)
    setModalMode('view')
    setModalMessage(null)
    setModalTab('info')
    setInventoryAddDraft(emptyInventoryDraft())
  }, [])

  const handleItemOpen = useCallback(async (item: AssetOrInventory) => {
    setSelectedItem(item)
    setEditingItem(item)
    setModalMode('view')
    setModalMessage(null)
    setGalleryIndex(0)
    setShowCheckTo(false)
    setModalTab('info')
    setModalOpen(true)
    if (item.source === 'Inventory' && item._id) void loadHistory(item._id)
  }, [loadHistory])

  const updateEditingField = useCallback((key: keyof AssetOrInventory, value: unknown) => {
    setEditingItem((prev) => (prev ? { ...prev, [key]: value } : prev))
  }, [])

  const updateEditingLocationAddress = useCallback((address: string) => {
    setEditingItem((prev) =>
      prev
        ? {
            ...prev,
            location: {
              ...(prev.location || {}),
              address,
            },
          }
        : prev,
    )
  }, [])

  const applyEditingJobLocation = useCallback((job: JobTreadJobOption | null) => {
    setEditingItem((prev) => {
      if (!prev) return prev
      return {
        ...prev,
        jobId: job?.id || '',
        jobNumber: job?.number || '',
        jobName: job?.name || '',
        project: job?.name || job?.number || '',
        location: {
          ...(prev.location || {}),
          address: locationAddressFromJob(job),
        },
      }
    })
  }, [])

  const handleUploadImages = useCallback(
    async (files: FileList | null) => {
      if (!files?.length) return
      try {
        const nextImages = await compressImageFiles(files)
        setEditingItem((prev) => {
          if (!prev) return prev
          const existing = Array.isArray(prev.imageUrls) ? prev.imageUrls : getImageUrls(prev)
          return { ...prev, imageUrls: [...existing, ...nextImages].filter(Boolean) }
        })
        setModalMessage(null)
      } catch (err: unknown) {
        const msg = errorMessage(err, '')
        setModalMessage(
          /memory|allocate|quota/i.test(msg)
            ? 'Not enough memory to process that photo. Choose a smaller image from the gallery.'
            : msg || 'Some images could not be processed. Please try smaller files.',
        )
      }
    },
    [],
  )

  const handleAddImageDataUrls = useCallback((dataUrls: string[]) => {
    if (!dataUrls.length) return
    setEditingItem((prev) => {
      if (!prev) return prev
      const existing = Array.isArray(prev.imageUrls) ? prev.imageUrls : getImageUrls(prev)
      return { ...prev, imageUrls: [...existing, ...dataUrls].filter(Boolean) }
    })
    setModalMessage(null)
  }, [])

  const removeEditingImage = useCallback((idx: number) => {
    setEditingItem((prev) => {
      if (!prev) return prev
      const existing = Array.isArray(prev.imageUrls) ? prev.imageUrls : getImageUrls(prev)
      return { ...prev, imageUrls: existing.filter((_, i) => i !== idx) }
    })
  }, [])

  const handleUploadDocuments = useCallback(async (files: FileList | null) => {
    if (!files?.length) return
    try {
      const docs = await readDocumentFiles(files)
      setEditingItem((prev) => {
        if (!prev) return prev
        const existing = getDocuments(prev)
        return { ...prev, documents: [...existing, ...docs] }
      })
      setModalMessage(null)
    } catch (err: unknown) {
      setModalMessage(errorMessage(err, 'Could not attach document.'))
    }
  }, [])

  const removeEditingDocument = useCallback((idx: number) => {
    setEditingItem((prev) => {
      if (!prev) return prev
      const existing = getDocuments(prev)
      return { ...prev, documents: existing.filter((_, i) => i !== idx) }
    })
  }, [])

  const openCreateModal = useCallback((source: 'Asset' | 'Inventory') => {
    const base: AssetOrInventory =
      source === 'Asset'
        ? {
            _id: '',
            name: '',
            type: 'vehicle',
            source,
            status: 'active',
            value: 0,
            imageUrls: [],
            assignedEmployeeId: '',
          }
        : {
            _id: '',
            name: '',
            type: 'inventory',
            source,
            status: 'in_stock',
            quantity: 1,
            value: 0,
            imageUrls: [],
            assignedEmployeeId: '',
          }
    setSelectedItem(base)
    setEditingItem(base)
    setModalMode('add')
    setModalMessage(null)
    setGalleryIndex(0)
    setShowCheckTo(false)
    setModalTab('info')
    setInventoryAddDraft(emptyInventoryDraft())
    setModalOpen(true)
  }, [])

  const openLookupScanner = useCallback(() => {
    setScannerPurpose('lookup')
    setScannerOpen(true)
  }, [])

  const openAddScanner = useCallback(() => {
    setScannerPurpose(modalMode === 'edit' ? 'edit' : 'add')
    setScannerOpen(true)
  }, [modalMode])

  const lookupByBarcode = useCallback(
    async (barcode: string) => {
      try {
        const found = await executeCapability<AssetOrInventory>({
          app: 'tally',
          capability: 'inventory.getByBarcode',
          input: { barcode },
        })
        const item = asTallyItem(found, 'Inventory')
        setInventory((prev) => {
          const exists = prev.some((x) => x._id === item._id)
          return exists ? prev.map((x) => (x._id === item._id ? item : x)) : [item, ...prev]
        })
        setScannerOpen(false)
        await handleItemOpen(item)
      } catch (err: unknown) {
        setScannerOpen(false)
        showToast(errorMessage(err, 'No inventory item found for that barcode'))
      }
    },
    [handleItemOpen, showToast],
  )

  const handleScanResult = useCallback(
    (barcode: string) => {
      const trimmed = barcode.trim()
      if (!trimmed) return
      if (scannerPurpose === 'lookup') {
        void lookupByBarcode(trimmed)
        return
      }
      if (scannerPurpose === 'edit') {
        updateEditingField('barcode', trimmed)
        setScannerOpen(false)
        return
      }
      setInventoryAddDraft((prev) => ({ ...prev, barcode: trimmed }))
      setScannerOpen(false)
    },
    [lookupByBarcode, scannerPurpose, updateEditingField],
  )

  const saveEditingItem = useCallback(async () => {
    if (!editingItem && !(modalMode === 'add' && selectedItem?.source === 'Inventory')) return

    setSavingItem(true)
    setModalMessage(null)
    try {
      if (modalMode === 'add' && selectedItem?.source === 'Inventory') {
        const draft = inventoryAddDraft
        if (!draft.barcode.trim()) {
          setModalMessage('Barcode is required.')
          setSavingItem(false)
          return
        }
        const jobName = draft.job?.name || ''
        const jobNumber = draft.job?.number || ''
        const defaultName =
          draft.name.trim() ||
          [jobNumber || jobName, draft.barcode.trim()].filter(Boolean).join(' · ') ||
          'Pallet'
        const update: Record<string, unknown> = {
          name: defaultName,
          type: 'inventory',
          status: draft.status || 'in_stock',
          quantity: Number.isFinite(draft.quantity) ? draft.quantity : 1,
          notes: draft.notes,
          barcode: draft.barcode.trim(),
          imageUrls: draft.imageUrls,
          documents: draft.documents,
        }
        if (draft.job) {
          const jobId = draft.job.id != null ? String(draft.job.id).trim() : ''
          const jobNumber = draft.job.number != null ? String(draft.job.number).trim() : ''
          const jobName = draft.job.name != null ? String(draft.job.name).trim() : ''
          if (jobId) update.jobId = jobId
          if (jobNumber) update.jobNumber = jobNumber
          if (jobName) update.jobName = jobName
          if (jobName || jobNumber) update.project = jobName || jobNumber
          const address = locationAddressFromJob(draft.job)
          if (address) update.location = { address }
        }
        const created = await executeCapability<AssetOrInventory>({
          app: 'tally',
          capability: 'inventory.create',
          input: update,
        })
        const saved = asTallyItem(created, 'Inventory')
        setInventory((prev) => [saved, ...prev])
        setSelectedItem(saved)
        setEditingItem(saved)
        setModalMode('view')
        setInventoryAddDraft(emptyInventoryDraft())
        setModalMessage('Item created.')
        void loadHistory(saved._id)
        return
      }

      if (!editingItem) return
      if (!editingItem.name?.trim()) {
        setModalMessage('Name is required.')
        setSavingItem(false)
        return
      }

      const source = editingItem.source
      const isAsset = source === 'Asset'
      const update: Record<string, unknown> = {
        name: editingItem.name,
        type: editingItem.type,
        assignedEmployeeId: editingItem.assignedEmployeeId || '',
        value: editingItem.value !== undefined ? Number(editingItem.value) : undefined,
        notes: editingItem.notes,
        status: editingItem.status,
        imageUrls: Array.isArray(editingItem.imageUrls) ? editingItem.imageUrls : [],
      }
      if (editingItem.location?.address) update.location = { address: editingItem.location.address }
      if (isAsset) {
        update.dateAssigned = editingItem.dateAssigned
        update.serialNumber = editingItem.serialNumber
        update.jobId = editingItem.jobId
        update.jobNumber = editingItem.jobNumber
        update.jobName = editingItem.jobName
      } else {
        update.quantity = editingItem.quantity !== undefined ? Number(editingItem.quantity) : 1
        update.sku = editingItem.sku
        update.barcode = editingItem.barcode
        update.jobId = editingItem.jobId
        update.jobNumber = editingItem.jobNumber
        update.jobName = editingItem.jobName
        update.project = editingItem.project
        update.documents = Array.isArray(editingItem.documents) ? editingItem.documents : []
      }
      Object.keys(update).forEach((k) => update[k] === undefined && delete update[k])

      let saved: AssetOrInventory
      if (modalMode === 'add') {
        const created = await executeCapability<AssetOrInventory>({
          app: 'tally',
          capability: isAsset ? 'assets.create' : 'inventory.create',
          input: update,
        })
        saved = asTallyItem(created, source)
      } else {
        const updated = await executeCapability<AssetOrInventory>({
          app: 'tally',
          capability: isAsset ? 'assets.update' : 'inventory.update',
          input: { id: editingItem._id, update },
        })
        saved = asTallyItem(updated, source)
      }

      if (saved.source === 'Asset') {
        setAssets((prev) => {
          const exists = prev.some((x) => x._id === saved._id)
          return exists ? prev.map((x) => (x._id === saved._id ? saved : x)) : [saved, ...prev]
        })
      } else {
        setInventory((prev) => {
          const exists = prev.some((x) => x._id === saved._id)
          return exists ? prev.map((x) => (x._id === saved._id ? saved : x)) : [saved, ...prev]
        })
      }

      setSelectedItem(saved)
      setEditingItem(saved)
      setModalMode('view')
      setModalMessage(
        modalMode === 'add'
          ? source === 'Inventory'
            ? 'Item created.'
            : 'Asset created.'
          : source === 'Inventory'
            ? 'Item updated.'
            : 'Asset updated.',
      )
      if (saved.source === 'Inventory' && saved._id) void loadHistory(saved._id)
    } catch (err: unknown) {
      setModalMessage(errorMessage(err, 'Failed to save entry'))
    } finally {
      setSavingItem(false)
    }
  }, [
    editingItem,
    inventoryAddDraft,
    loadHistory,
    modalMode,
    selectedItem,
  ])

  const deleteSelectedItem = useCallback(async () => {
    if (!selectedItem?._id) return
    setDeletingItem(true)
    setModalMessage(null)
    try {
      await executeCapability({
        app: 'tally',
        capability: selectedItem.source === 'Asset' ? 'assets.delete' : 'inventory.delete',
        input: { id: selectedItem._id },
      })
      if (selectedItem.source === 'Asset') {
        setAssets((prev) => prev.filter((x) => x._id !== selectedItem._id))
      } else {
        setInventory((prev) => prev.filter((x) => x._id !== selectedItem._id))
      }
      closeModal()
      setSelectedItem(null)
      setEditingItem(null)
    } catch (err: unknown) {
      setModalMessage(errorMessage(err, 'Failed to delete entry'))
    } finally {
      setDeletingItem(false)
    }
  }, [selectedItem, closeModal])

  const handleCheckTo = useCallback(
    async (job: JobTreadJobOption) => {
      if (!selectedItem?._id) return
      const updated = await executeCapability<AssetOrInventory>({
        app: 'tally',
        capability: 'inventory.checkTo',
        input: {
          id: selectedItem._id,
          jobId: job.id,
          jobNumber: job.number,
          jobName: job.name,
        },
      })
      const saved = asTallyItem(updated, 'Inventory')
      setSelectedItem(saved)
      setEditingItem(saved)
      setInventory((prev) => prev.map((x) => (x._id === saved._id ? saved : x)))
      setShowCheckTo(false)
      setModalMessage(`Checked to ${formatJobLabel(saved) || 'location'}.`)
      void loadHistory(saved._id)
    },
    [selectedItem, loadHistory],
  )

  const firstAllowedTab = useMemo<TallyTab | null>(() => {
    if (canViewFleet) return 'fleet'
    if (canViewInventory) return 'inventory'
    if (canViewAssets) return 'assets'
    return null
  }, [canViewAssets, canViewInventory, canViewFleet])

  useEffect(() => {
    if (!firstAllowedTab) return
    const tabAllowed =
      (tab === 'assets' && canViewAssets) ||
      (tab === 'inventory' && canViewInventory) ||
      (tab === 'fleet' && canViewFleet)
    if (!tabAllowed) setTab(firstAllowedTab)
  }, [tab, canViewAssets, canViewInventory, canViewFleet, firstAllowedTab])

  useEffect(() => {
    if (!authLoading && !hasAnyTallyPermission) {
      navigate('/', { replace: true })
    }
  }, [authLoading, hasAnyTallyPermission, navigate])

  const employeeOptionById = useMemo(
    () =>
      employeeOptions.reduce<Record<string, EmployeeAssignmentOption>>((acc, option) => {
        acc[option.employeeId] = option
        return acc
      }, {}),
    [employeeOptions],
  )

  const getAssignmentLabel = useCallback(
    (item: AssetOrInventory): string | null => {
      const assignedEmployeeId = String(item.assignedEmployeeId || '').trim()
      if (assignedEmployeeId) {
        return employeeOptionById[assignedEmployeeId]?.displayName || `Employee ${assignedEmployeeId}`
      }
      const legacyAssignedTo = String(item.assignedTo || '').trim()
      if (
        legacyAssignedTo &&
        legacyAssignedTo.toLowerCase() !== String(item.name || '').trim().toLowerCase() &&
        // Don't treat a JobTread job label stored in legacy assignedTo as an employee
        legacyAssignedTo !== String(item.jobName || '').trim() &&
        legacyAssignedTo !== formatJobLabel(item)
      ) {
        return legacyAssignedTo
      }
      return null
    },
    [employeeOptionById],
  )

  const activeItems = useMemo(
    () => (tab === 'assets' ? assets : tab === 'inventory' ? inventory : []),
    [tab, assets, inventory],
  )
  const activeLoading = tab === 'assets' ? loadingAssets : loadingInventory
  const activeError = tab === 'assets' ? errorAssets : errorInventory
  const isAssetTab = tab === 'assets'

  const filteredItems = useMemo(() => {
    const queries = [...filters]
    if (search.trim()) queries.push(search.trim())
    if (!queries.length) return activeItems
    return activeItems.filter((item) => {
      const haystack = buildTallySearchHaystack(item, {
        assignmentLabel: getAssignmentLabel(item),
      })
      return queries.every((q) => matchesTallySearch(haystack, q))
    })
  }, [activeItems, filters, search, getAssignmentLabel])
  const sortedItems = useMemo(
    () => [...filteredItems].sort((a, b) => a.name.localeCompare(b.name)),
    [filteredItems],
  )

  const visibleIds = useMemo(() => sortedItems.map((item) => item._id), [sortedItems])

  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev
      const visible = new Set(visibleIds)
      let changed = false
      const next = new Set<string>()
      prev.forEach((id) => {
        if (visible.has(id)) next.add(id)
        else changed = true
      })
      return changed ? next : prev
    })
  }, [visibleIds])

  const selectedItems = useMemo(
    () => activeItems.filter((item) => selectedIds.has(item._id)),
    [activeItems, selectedIds],
  )

  const handleBulkDelete = useCallback(async () => {
    if (selectedItems.length === 0) return
    setBulkBusy(true)
    let deleted = 0
    let failed = 0
    for (const item of selectedItems) {
      try {
        await executeCapability({
          app: 'tally',
          capability: item.source === 'Asset' ? 'assets.delete' : 'inventory.delete',
          input: { id: item._id },
        })
        deleted += 1
        if (item.source === 'Asset') {
          setAssets((prev) => prev.filter((x) => x._id !== item._id))
        } else {
          setInventory((prev) => prev.filter((x) => x._id !== item._id))
        }
      } catch {
        failed += 1
      }
    }
    setBulkBusy(false)
    setBulkDeleteOpen(false)
    clearSelection()
    const parts = [`Deleted ${deleted}`]
    if (failed) parts.push(`${failed} failed`)
    showToast(parts.join(' · '))
  }, [clearSelection, selectedItems, showToast])

  const handleBulkEditApply = useCallback(
    async (values: TallyBulkEditValues) => {
      if (selectedItems.length === 0) return
      setBulkBusy(true)
      let updated = 0
      let failed = 0

      for (const item of selectedItems) {
        const isAsset = item.source === 'Asset'
        const update: Record<string, unknown> = {}

        if (values.clearAssignedEmployee) {
          update.assignedEmployeeId = ''
        } else if (values.assignedEmployeeId !== undefined) {
          update.assignedEmployeeId = values.assignedEmployeeId
        }

        if (values.clearLocation) {
          update.jobId = ''
          update.jobNumber = ''
          update.jobName = ''
          update.project = ''
          update.location = { address: '' }
        } else if (values.job) {
          update.jobId = values.job.id || ''
          update.jobNumber = values.job.number || ''
          update.jobName = values.job.name || ''
          update.project = values.job.name || values.job.number || ''
          update.location = { address: locationAddressFromJob(values.job) }
        }

        if (!isAsset) {
          if (values.status !== undefined) update.status = values.status
          if (values.quantity !== undefined) update.quantity = values.quantity
        } else {
          if (values.value !== undefined) update.value = values.value
          if (values.notes !== undefined) update.notes = values.notes
        }

        if (Object.keys(update).length === 0) continue

        try {
          const saved = await executeCapability<AssetOrInventory>({
            app: 'tally',
            capability: isAsset ? 'assets.update' : 'inventory.update',
            input: { id: item._id, update },
          })
          const next = asTallyItem(saved, item.source)
          if (isAsset) {
            setAssets((prev) => prev.map((x) => (x._id === next._id ? next : x)))
          } else {
            setInventory((prev) => prev.map((x) => (x._id === next._id ? next : x)))
          }
          updated += 1
        } catch {
          failed += 1
        }
      }

      setBulkBusy(false)
      setBulkEditOpen(false)
      clearSelection()
      const parts = [`Updated ${updated}`]
      if (failed) parts.push(`${failed} failed`)
      showToast(parts.join(' · '))
    },
    [clearSelection, selectedItems, showToast],
  )

  if (authLoading) {
    return (
      <div className="min-h-screen bg-base-100 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <FontAwesomeIcon icon={faSpinner} className="text-4xl text-primary animate-spin" />
          <span className="text-lg">Loading...</span>
        </div>
      </div>
    )
  }

  if (!hasAnyTallyPermission) return null

  const showMobileActions =
    (tab === 'inventory' && canViewInventory) ||
    (tab === 'assets' && canViewAssets && canManageTally)

  return (
    <div className="flex h-screen max-h-screen min-h-0 flex-col overflow-hidden bg-base-100">
      <div className="w-full shrink-0">
        <Navbar pageTitle="Tally" />
      </div>
      <main className="container mx-auto flex min-h-0 w-full max-w-full flex-1 flex-col overflow-hidden px-4 py-3 sm:py-4">

        <div role="tablist" className="tabs tabs-lift mb-3 shrink-0">
          {canViewFleet ? (
            <button
              type="button"
              role="tab"
              className={`tab ${tab === 'fleet' ? 'tab-active' : ''}`}
              onClick={() => {
                setTab('fleet')
                setSearch('')
                setFilters([])
                clearSelection()
              }}
            >
              Fleet
            </button>
          ) : null}
          {canViewInventory ? (
            <button
              type="button"
              role="tab"
              className={`tab ${tab === 'inventory' ? 'tab-active' : ''}`}
              onClick={() => {
                setTab('inventory')
                setSearch('')
                setFilters([])
                clearSelection()
              }}
            >
              Inventory
            </button>
          ) : null}
          {canViewAssets ? (
            <button
              type="button"
              role="tab"
              className={`tab ${tab === 'assets' ? 'tab-active' : ''}`}
              onClick={() => {
                setTab('assets')
                setSearch('')
                setFilters([])
                clearSelection()
              }}
            >
              Assets
            </button>
          ) : null}
        </div>

        {tab === 'fleet' && canViewFleet ? (
          <FleetTab canManage={canManageTally} active={tab === 'fleet' && canViewFleet} />
        ) : null}

        <div
          className={
            (tab === 'assets' && canViewAssets) || (tab === 'inventory' && canViewInventory)
              ? 'flex min-h-0 flex-1 flex-col overflow-hidden'
              : 'hidden'
          }
        >
          <TallyToolbar
            isAssetTab={isAssetTab}
            search={search}
            onSearchChange={setSearch}
            onSearchCommit={(value) => {
              setFilters((f) => (f.includes(value) ? f : [...f, value]))
              setSearch('')
            }}
            filters={filters}
            onRemoveFilter={(idx) => setFilters((f) => f.filter((_, i) => i !== idx))}
            onClearSearch={() => {
              setSearch('')
              setFilters([])
            }}
            resultCount={sortedItems.length}
            totalCount={activeItems.length}
            collapsedView={collapsedView}
            onToggleCollapsedView={() => {
              setCollapsedView((v) => {
                if (v) clearSelection()
                return !v
              })
            }}
            canManage={canManageTally}
            onAdd={() => openCreateModal(isAssetTab ? 'Asset' : 'Inventory')}
            showScan={!isAssetTab && canViewInventory}
            onScan={openLookupScanner}
          />

          {canManageTally && collapsedView ? (
            <TallyBulkBar
              selectedCount={selectedIds.size}
              deletableCount={selectedItems.length}
              linxupSkippedCount={0}
              onClear={clearSelection}
              onEdit={() => setBulkEditOpen(true)}
              onDelete={() => setBulkDeleteOpen(true)}
            />
          ) : null}

          <div className="min-h-0 flex-1 overflow-y-auto pr-1">
            <TallyItemList
              items={sortedItems}
              loading={activeLoading}
              error={activeError}
              isAssetTab={isAssetTab}
              collapsedView={collapsedView}
              getAssignmentLabel={getAssignmentLabel}
              onOpen={(item) => {
                void handleItemOpen(item)
              }}
              selectionEnabled={canManageTally && collapsedView}
              selectedIds={selectedIds}
              onToggleSelect={toggleSelect}
              onToggleSelectAll={(selectAll) => toggleSelectAll(visibleIds, selectAll)}
            />
          </div>
        </div>
      </main>

      <div className="shrink-0">
        <TallyMobileActions
          visible={showMobileActions}
          canAdd={canManageTally}
          showScan={tab === 'inventory' && canViewInventory}
          onAdd={() => openCreateModal(isAssetTab ? 'Asset' : 'Inventory')}
          onScan={openLookupScanner}
        />
        <Footer className="!mt-0" />
      </div>

      <TallyItemModal
        open={modalOpen}
        item={selectedItem}
        editingItem={editingItem}
        mode={modalMode}
        canManage={canManageTally}
        employeeOptions={employeeOptions}
        getAssignmentLabel={getAssignmentLabel}
        modalMessage={modalMessage}
        saving={savingItem}
        deleting={deletingItem}
        inventoryAddDraft={inventoryAddDraft}
        onInventoryAddDraftChange={(patch) =>
          setInventoryAddDraft((prev) => ({
            ...prev,
            ...(typeof patch === 'function' ? patch(prev) : patch),
          }))
        }
        onOpenBarcodeScanner={openAddScanner}
        showCheckTo={showCheckTo}
        onShowCheckTo={setShowCheckTo}
        onCheckTo={handleCheckTo}
        history={history}
        historyLoading={historyLoading}
        modalTab={modalTab}
        onModalTabChange={setModalTab}
        galleryIndex={galleryIndex}
        onGalleryIndexChange={setGalleryIndex}
        onClose={closeModal}
        onEdit={() => {
          setEditingItem(selectedItem)
          setModalMode('edit')
          setModalMessage(null)
          setShowCheckTo(false)
        }}
        onSave={() => {
          void saveEditingItem()
        }}
        onDelete={() => {
          void deleteSelectedItem()
        }}
        onUpdateField={updateEditingField}
        onUpdateLocationAddress={updateEditingLocationAddress}
        onApplyJobLocation={applyEditingJobLocation}
        onUploadImages={(files) => {
          void handleUploadImages(files)
        }}
        onAddImageDataUrls={handleAddImageDataUrls}
        onRemoveImage={removeEditingImage}
        onUploadDocuments={(files) => {
          void handleUploadDocuments(files)
        }}
        onRemoveDocument={removeEditingDocument}
      />

      <TallyBulkEditModal
        open={bulkEditOpen}
        isAssetTab={isAssetTab}
        selectedCount={selectedIds.size}
        employeeOptions={employeeOptions}
        saving={bulkBusy}
        onClose={() => {
          if (!bulkBusy) setBulkEditOpen(false)
        }}
        onApply={(values) => {
          void handleBulkEditApply(values)
        }}
      />

      <TallyBulkDeleteConfirm
        open={bulkDeleteOpen}
        selectedCount={selectedIds.size}
        deletableCount={selectedItems.length}
        linxupSkippedCount={0}
        deleting={bulkBusy}
        onCancel={() => {
          if (!bulkBusy) setBulkDeleteOpen(false)
        }}
        onConfirm={() => {
          void handleBulkDelete()
        }}
      />

      <BarcodeScanner
        open={scannerOpen}
        title={scannerPurpose === 'lookup' ? 'Scan pallet barcode' : 'Scan barcode'}
        onClose={() => setScannerOpen(false)}
        onScan={handleScanResult}
      />

      {toast ? (
        <div className="toast toast-top toast-center z-[70]">
          <div className="alert alert-warning shadow-lg">
            <span>{toast}</span>
          </div>
        </div>
      ) : null}
    </div>
  )
}

export default TallyPage
